import * as React from 'react'

/* TYPES */

interface InjectedScriptConfig {
  id: string
  src: string
}

interface InjectedScriptApi {
  isLoaded: boolean
  setLoaded: (isLoaded: boolean) => void
}

interface Props extends InjectedScriptConfig {
  children?: React.ReactNode
}

interface RenderProps extends InjectedScriptConfig {
  children?: ((props: InjectedScriptApi) => React.ReactNode) | null
}

/* HOOK */

export const useInjectedScript = ({ id, src }: InjectedScriptConfig) => {
  const [isLoaded, setLoaded] = React.useState(false)

  React.useEffect(() => {
    const existingScript = document.getElementById(id)

    if (existingScript) {
      setLoaded(true)
    } else {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.id = id
      script.onload = () => {
        setLoaded(true)
      }
      script.src = src

      document.head && document.head.appendChild(script)
    }
  }, [id, src])

  return { isLoaded, setLoaded }
}

/* DECORATOR */

export const withInjectedScript = <T extends {}>(
  config: InjectedScriptConfig
) => (Component: React.ComponentType<T>) => (
  props: T & { injectedScript: InjectedScriptConfig }
) => (
  <Component
    {...props}
    injectedScript={useInjectedScript({ ...config, ...props.injectedScript })}
  />
)

/* RENDER PROP */

export const InjectedScript = ({ children = null, ...props }: RenderProps) => {
  const injectedScript = useInjectedScript(props)

  return typeof children === 'function' ? children(injectedScript) : children
}

/* CONTEXT */

const noop = (method: String) => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in a \`<InjectedScriptProvider>\``
  )

export const InjectedScriptContext = React.createContext<InjectedScriptApi>({
  isLoaded: false,
  setLoaded: noop('setLoaded'),
})

export const InjectedScriptProvider = ({ children, ...props }: Props) => (
  <InjectedScriptContext.Provider value={useInjectedScript(props)}>
    {children}
  </InjectedScriptContext.Provider>
)

export const consumeInjectedScript = () =>
  React.useContext(InjectedScriptContext)
